import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { RadioStation } from '../types/radio';
import { saveLastStation, savePlaybackState, getLastStation, getPlaybackState } from '../utils/storage';
import { audioEngine } from '../services/audioEngine';
import type { UnplayableReason } from '../utils/streamUrl';

interface PlayerState {
  currentStation: RadioStation | null;
  isPlaying: boolean;
  volume: number;
  isFullscreen: boolean;
  sleepTimer: number | null;
  isConnected: boolean;
  isBuffering: boolean;
  connectionQuality: 'good' | 'fair' | 'poor';
  hasUserInteracted: boolean;
}

interface PlayerStore extends PlayerState {
  setCurrentStation: (station: RadioStation | null) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setVolume: (volume: number) => void;
  setIsFullscreen: (isFullscreen: boolean) => void;
  resetPlayer: () => void;
  restoreLastSession: () => void;
  setSleepTimer: (minutes: number | null) => void;
  setIsConnected: (isConnected: boolean) => void;
  setIsBuffering: (isBuffering: boolean) => void;
  setConnectionQuality: (quality: 'good' | 'fair' | 'poor') => void;
  setHasUserInteracted: (hasInteracted: boolean) => void;
  streamError: boolean;
  unplayableReason: UnplayableReason | null;
  setStreamStatus: (status: { streamError: boolean; unplayableReason: UnplayableReason | null }) => void;
  isMuted: boolean;
  setIsMuted: (isMuted: boolean) => void;
}

// Default state values
const defaultState: PlayerState = {
  currentStation: null,
  isPlaying: false,
  volume: 0.8,
  isFullscreen: false,
  sleepTimer: null,
  isConnected: false,
  isBuffering: true,
  connectionQuality: 'poor',
  hasUserInteracted: false
};

// Get saved state from storage
const savedState = {
  currentStation: getLastStation(),
  ...getPlaybackState()
};

// Combine default and saved state, preferring saved values
const initialState: PlayerState = {
  ...defaultState,
  ...savedState
};

export const usePlayerStore = create<PlayerStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      streamError: false,
      unplayableReason: null,
      isMuted: false,

      setCurrentStation: (station) => {
        saveLastStation(station);
        // Clear any previous failure so the new station starts from a clean slate.
        set({
          currentStation: station,
          isPlaying: station !== null,
          streamError: false,
          unplayableReason: null,
        });
        if (!station) audioEngine.stop();
      },

      setIsPlaying: (isPlaying) => {
        // If this is a user-initiated play action, mark as interacted
        if (isPlaying) {
          set({ hasUserInteracted: true });
        }

        savePlaybackState(get().volume, isPlaying);
        set({ isPlaying });
      },

      setVolume: (volume) => {
        const newVolume = Math.max(0, Math.min(1, volume));
        savePlaybackState(newVolume, get().isPlaying);
        audioEngine.setVolume(newVolume);
        set({ volume: newVolume });
      },

      setIsFullscreen: (isFullscreen) => set({ isFullscreen }),

      resetPlayer: () => {
        audioEngine.stop();
        saveLastStation(null);
        savePlaybackState(defaultState.volume, false);
        set({ ...defaultState, streamError: false, unplayableReason: null });
      },

      restoreLastSession: () => {
        const lastStation = getLastStation();
        const { volume } = getPlaybackState();
        if (!lastStation) return;

        // Restore the station and volume, but never auto-resume audio. Browsers
        // reject play() without a user gesture, and the old auto-resume path was
        // a common source of phantom playback after a refresh.
        set({
          currentStation: lastStation,
          volume,
          isPlaying: false,
        });
        audioEngine.setVolume(volume);
      },

      setSleepTimer: (minutes) => {
        // 0 means "the timer just ran out": silence the audio, then clear the
        // timer entirely.
        //
        // It must NOT be left sitting at 0. That value used to persist to
        // localStorage and gate both setIsPlaying and setCurrentStation, so once
        // a timer finished nothing could ever be played again — not by pressing
        // play, not by choosing another station, and not even after a reload,
        // because the 0 came straight back out of storage.
        if (minutes === 0) {
          audioEngine.stop();
          set({ sleepTimer: null, isPlaying: false });
          savePlaybackState(get().volume, false);
        } else {
          set({ sleepTimer: minutes });
        }
      },

      setIsConnected: (isConnected) => set({ isConnected }),

      setIsBuffering: (isBuffering) => set({ isBuffering }),

      setConnectionQuality: (quality) => set({ connectionQuality: quality }),

      setHasUserInteracted: (hasInteracted: boolean) => {
        set({ hasUserInteracted: hasInteracted });
      },

      setStreamStatus: ({ streamError, unplayableReason }) => {
        set({ streamError, unplayableReason });
      },

      setIsMuted: (isMuted: boolean) => {
        set({ isMuted });
        audioEngine.setMuted(isMuted);
      },
    }),
    {
      name: 'player-storage',
      version: 1,
      // sleepTimer is deliberately not persisted. A sleep timer shouldn't outlive
      // the page, and sleepTimerService already keeps its own durable state under
      // `sleepTimerState` for a timer that is genuinely still running.
      partialize: (state) => ({
        volume: state.volume,
        currentStation: state.currentStation,
        wasPlaying: state.isPlaying, // Also persist playing state
      }),
      // Anyone who already has a finished timer stored as 0 would otherwise stay
      // permanently unable to play anything, since the old value is merged back in
      // on rehydrate. Drop it on upgrade.
      migrate: (persisted) => {
        const state = { ...(persisted as Record<string, unknown>) };
        delete state.sleepTimer;
        return state;
      },
    }
  )
);